import type { ImportError } from '../../types/import';

interface ScanErrorPanelProps {
  error: ImportError;
  onScanAgain: () => void;
  onRetry: () => void;
  onFallback: () => void;
}

function getErrorIcon(type: ImportError['type']): string {
  switch (type) {
    case 'invalid-qr':
      return '⚠️';
    case 'timeout':
      return '⏱️';
    case 'network':
      return '📡';
    case 'api-error':
    case 'no-product-data':
      return '❌';
    case 'appsync-error':
      return '🔌';
    default:
      return '⚠️';
  }
}

/**
 * Determine if this error is a scan-phase error (invalid QR, scan timeout)
 * vs a lookup-phase error (network, API timeout, appsync, etc.)
 */
function isScanPhaseError(type: ImportError['type']): boolean {
  return type === 'invalid-qr';
}

/**
 * For scan-phase errors the user action is "Scan Again" (re-scan a new QR code).
 * For lookup-phase errors the user action is "Retry" (re-attempt the API lookup).
 * The scan timeout with retryCount === 0 is the 15s QR detection timeout (scan phase).
 */
function isLookupPhaseRetry(error: ImportError): boolean {
  // Scan-phase errors always use "Scan Again"
  if (isScanPhaseError(error.type)) return false;
  // Timeout with retryCount 0 and the help message is the 15s scan timeout
  if (error.type === 'timeout' && error.retryCount === 0 && error.message.includes('repositioning')) {
    return false;
  }
  return true;
}

export function ScanErrorPanel({
  error,
  onScanAgain,
  onRetry,
  onFallback,
}: ScanErrorPanelProps) {
  const icon = getErrorIcon(error.type);
  const showAsRetry = isLookupPhaseRetry(error);

  return (
    <div className="scan-error-panel" role="alert">
      <div className="scan-error-icon" aria-hidden="true">
        {icon}
      </div>
      <p className="scan-error-message">{error.message}</p>
      <div className="scan-error-actions">
        {error.canRetry && showAsRetry && (
          <button
            className="cta-button"
            onClick={onRetry}
            type="button"
          >
            Retry
          </button>
        )}
        {error.canRetry && !showAsRetry && (
          <button
            className="cta-button"
            onClick={onScanAgain}
            type="button"
          >
            Scan Again
          </button>
        )}
        <button
          className="cta-button-secondary"
          onClick={onFallback}
          type="button"
        >
          Enter Manually
        </button>
      </div>
    </div>
  );
}
